import {
  ApiPaginatedResponse,
  ApiQuery,
} from '@kinet-open-network/client/core/api-client';
import {
  FieldBuilderSelectLazy,
  selectLazy,
  SelectLazyColumn,
} from '@kinet-open-network/client/core/forms/ui';
import { Observable } from 'rxjs';
import { Delivery } from '../models/delivery.model';

export interface SelectLazyDeliveriesOptions<M = any> {
  service: {
    query: (
      q?: ApiQuery | null,
      model?: any,
    ) => Observable<ApiPaginatedResponse<Delivery>>;
  };
  key?: string;
  label?: string;
  initValueField?: keyof M | string;
  forceFilters?: string;
  columns?: (string | SelectLazyColumn<Delivery>)[];
  additionalFields?: string[] | '*';
  panelStyle?: Record<string, string>;
  showClear?: boolean;
}

export const DEFAULT_DELIVERY_COLUMNS: SelectLazyColumn<Delivery>[] = [
  { key: 'id', label: 'ID', width: '100px' },
  { key: 'deliveryMode.name', label: 'Spôsob dodania' },
  {
    key: 'date',
    label: 'Dátum',
    width: '100px',
    getValue: (item: Delivery) => item?.date?.toLocaleDateString() ?? '',
  },
];

export function selectLazyDeliveries<M = any>(
  serviceOrOptions:
    | SelectLazyDeliveriesOptions<M>['service']
    | SelectLazyDeliveriesOptions<M>,
  options?: Omit<SelectLazyDeliveriesOptions<M>, 'service'>,
): FieldBuilderSelectLazy<Delivery> {
  const config: SelectLazyDeliveriesOptions<M> =
    typeof serviceOrOptions === 'object' && 'query' in serviceOrOptions
      ? { service: serviceOrOptions, ...options }
      : (serviceOrOptions as SelectLazyDeliveriesOptions<M>);

  const key = config.key ?? 'deliveryId';
  const label = config.label ?? 'Dodanie';
  const initValueField = config.initValueField ?? 'delivery';
  const forceFilters = config.forceFilters;
  const columns = config.columns ?? DEFAULT_DELIVERY_COLUMNS;
  const additionalFields = config.additionalFields ?? '*';

  const builder = selectLazy<Delivery, M>(
    key,
    label,
    'name',
    'id',
    initValueField as keyof M,
  )
    .columns(columns)
    .query(
      {
        query: (q, model) =>
          config.service.query({ ...q, sort: ['-id'] }, model),
      },
      additionalFields,
      (item: Delivery) =>
        `${item.id} ${item.deliveryMode?.name ?? ''} (${item.date?.toLocaleDateString() ?? ''})`,
      forceFilters,
    );

  const extraProps: Record<string, any> = {};
  if (config.panelStyle) extraProps['panelStyle'] = config.panelStyle;
  if (config.showClear !== undefined)
    extraProps['showClear'] = config.showClear;

  if (Object.keys(extraProps).length > 0) {
    builder.props(extraProps);
  }

  return builder;
}
